'use client';

import { useState } from 'react';
import Navigation from '@/components/Navigation';
import { MOCK_CAR_MAKES, MOCK_CAR_MODELS, type CarMake } from '@/app/lib/mock-db';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { Card } from '@/components/ui/card';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, Calendar, ChevronLeft, Car } from 'lucide-react';

export default function BuyMakePage() {
  const router = useRouter();
  const [selectedMake, setSelectedMake] = useState<CarMake | null>(null);
  const [searchModel, setSearchModel] = useState('');
  const [yearFrom, setYearFrom] = useState('');
  const [yearTo, setYearTo] = useState('');

  const filteredModels = MOCK_CAR_MODELS.filter(model => 
    model.make_id === selectedMake?.id &&
    (model.name_ar.includes(searchModel) || model.name_en.toLowerCase().includes(searchModel.toLowerCase()))
  );

  const handleApplyFilters = (modelId?: string) => {
    const params = new URLSearchParams();
    if (selectedMake) params.set('make', selectedMake.id);
    if (modelId) params.set('model', modelId);
    if (yearFrom) params.set('yearFrom', yearFrom);
    if (yearTo) params.set('yearTo', yearTo);
    
    router.push(`/buy/results?${params.toString()}`);
    setSelectedMake(null);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navigation title="اختر ماركة السيارة" />
      
      <div className="p-4 max-w-4xl mx-auto space-y-6">
        <div className="bg-white p-6 rounded-[2.5rem] shadow-sm border border-slate-100">
          <h2 className="text-xl font-bold font-headline mb-2">تسوق حسب الماركة</h2>
          <p className="text-sm text-muted-foreground">اختر الماركة التي تفضلها لاستعراض الموديلات المتاحة</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {MOCK_CAR_MAKES.map((make) => (
            <button key={make.id} onClick={() => setSelectedMake(make)} className="text-right">
              <Card className="p-6 flex flex-col items-center justify-center border-none shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all rounded-[2.5rem] bg-white group w-full h-full">
                <div className="relative w-20 h-20 mb-4 transition-transform group-hover:scale-110">
                  <Image 
                    src={make.logo_url} 
                    alt={make.name_ar} 
                    fill 
                    className="object-contain"
                    sizes="80px"
                  />
                </div>
                <span className="font-bold text-slate-800">{make.name_ar}</span>
                <span className="text-[10px] text-slate-400 font-bold uppercase mt-1">{make.name_en}</span>
              </Card>
            </button>
          ))}
        </div>
      </div>

      <Dialog open={!!selectedMake} onOpenChange={(open) => !open && setSelectedMake(null)}>
        <DialogContent className="rounded-[2.5rem] max-w-2xl p-0 overflow-hidden border-none shadow-2xl">
          <DialogHeader className="p-6 bg-slate-900 text-white">
            <div className="flex items-center gap-4">
              {selectedMake && (
                <div className="relative w-12 h-12 bg-white rounded-xl p-2">
                  <Image src={selectedMake.logo_url} alt="" fill className="object-contain p-1" />
                </div>
              )}
              <DialogTitle className="text-xl font-bold font-headline">
                {selectedMake?.name_ar} - اختر الموديل والسنة
              </DialogTitle>
            </div>
          </DialogHeader>

          <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
            {/* Year Filter */}
            <div className="space-y-3">
              <label className="text-sm font-bold flex items-center gap-2">
                <Calendar className="h-4 w-4 text-primary" /> سنة الصنع
              </label>
              <div className="grid grid-cols-2 gap-4">
                <Input 
                  placeholder="من سنة (مثلاً 2015)" 
                  type="number" 
                  value={yearFrom}
                  onChange={(e) => setYearFrom(e.target.value)}
                  className="rounded-2xl h-12 bg-slate-50 border-none"
                />
                <Input 
                  placeholder="إلى سنة (مثلاً 2024)" 
                  type="number" 
                  value={yearTo}
                  onChange={(e) => setYearTo(e.target.value)}
                  className="rounded-2xl h-12 bg-slate-50 border-none"
                />
              </div>
            </div>

            {/* Model Selection */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="text-sm font-bold flex items-center gap-2">
                  <Car className="h-4 w-4 text-primary" /> الموديلات المتوفرة
                </label>
                <div className="relative w-48">
                  <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-3 w-3 text-slate-400" />
                  <Input 
                    placeholder="ابحث عن موديل..." 
                    className="h-9 pr-8 rounded-xl text-xs bg-slate-50 border-none"
                    value={searchModel}
                    onChange={(e) => setSearchModel(e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <button 
                  onClick={() => handleApplyFilters()}
                  className="p-4 rounded-2xl border-2 border-dashed border-slate-200 hover:border-primary hover:bg-blue-50 transition-all text-center group"
                >
                  <span className="block font-bold text-sm group-hover:text-primary">جميع موديلات {selectedMake?.name_ar}</span>
                </button>
                {filteredModels.map((model) => (
                  <button 
                    key={model.id} 
                    onClick={() => handleApplyFilters(model.id)}
                    className="p-4 rounded-2xl bg-slate-50 hover:bg-white hover:shadow-md border border-transparent hover:border-primary/20 transition-all text-right group"
                  >
                    <span className="block font-bold text-sm group-hover:text-primary">{model.name_ar}</span>
                    <span className="block text-[10px] text-slate-400 font-bold uppercase">{model.name_en}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter className="p-6 bg-slate-50 border-t">
            <Button 
              className="w-full h-14 rounded-2xl bg-primary text-lg font-bold shadow-lg shadow-primary/20"
              onClick={() => handleApplyFilters()}
            >
              عرض النتائج المتاحة
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
